import { Button, Form, Input } from "@light-technology/components";
import { Controller, useForm } from "react-hook-form";
import { useSearchParams } from "react-router";
import { api } from "../../state/api";
import { useDocumentTitle } from "../../utilities/useDocumentTitle";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";
import { emailFieldRules } from "../../utilities/validation";

type ForgotPasswordForm = {
  email: string;
};

export const ForgotPasswordPage = () => {
  useDocumentTitle("Forgot password");

  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";

  const [forgotPassword, forgotPasswordResult] = useMutationWithToast(
    api.useForgotPasswordMutation(),
    {},
  );

  const { getValues, handleSubmit, control } = useForm<ForgotPasswordForm>({
    defaultValues: { email },
  });
  const onSubmit = handleSubmit(({ email }) => {
    forgotPassword({ email });
  });

  return !forgotPasswordResult.isSuccess ? (
    <Form
      className="flex size-full flex-col justify-center"
      validationBehavior="aria"
      onSubmit={onSubmit}
    >
      <div className="text-2xl font-semibold">What's your email?</div>
      <div className="mt-md text-tertiary">
        We'll send you password reset instructions.
      </div>
      <Controller
        control={control}
        name="email"
        rules={emailFieldRules}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            className="mt-3xl"
            type="email"
            placeholder="Enter your email"
            isRequired
            validationBehavior="aria"
            isInvalid={fieldState.invalid}
            hint={fieldState.error?.message}
          />
        )}
      />
      <Button
        className="mt-3xl mb-lg w-full"
        type="submit"
        size="lg"
        isLoading={forgotPasswordResult.isLoading}
      >
        Reset password
      </Button>
      <Button
        className="w-full"
        color="tertiary"
        size="lg"
        href={`/login?${searchParams}`}
      >
        Return to login
      </Button>
    </Form>
  ) : (
    <div className="flex size-full flex-col justify-center">
      <div className="text-2xl font-semibold">Check your email</div>
      <div className="mt-md text-tertiary">
        <span>If an account using </span>
        <span className="font-semibold">{getValues("email")} </span>
        <span>
          is found, we'll send an email with instructions on how to reset your
          password.
        </span>
      </div>
      <Button
        className="mt-3xl"
        size="lg"
        color="secondary"
        href={`/login?${searchParams}`}
      >
        Return to login
      </Button>
    </div>
  );
};
